/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import { instantInZone } from '@sportninja/common/ts-utils/luxon-permanent-dst'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { formatEventLocation } from '@sportninja/common/features/event/formatEventLocation'
import { getEventCategory } from '@sportninja/common/features/event/eventCategories'

import {
  font,
  weightMedium,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import type { CalendarEventCardProps } from './types'

/**
 * An event as it appears inside a calendar cell.
 *
 * Deliberately built to `CalendarGameCard`'s dimensions — same padding, same
 * font sizes, same truncation behaviour — so a cell holding one of each stays
 * on a single rhythm and the month grid's "+N more" cut-off lands in the same
 * place whichever kind fills the cell.
 *
 * What separates it from a game card is the LEFT BORDER, and that alone
 * carries the distinction. Games use `PRIMARY` (or `NEUTRAL_100` when TBD), so
 * an event takes `INFO_300` — a hue no game card can ever show. It is a fixed
 * colour rather than the event's category colour: the border's job is to say
 * "this is an event", and sourcing it from a 14-colour categorical palette
 * would make an uncategorised event indistinguishable from a game. The
 * category is carried by its own badge instead, where a colour that varies is
 * exactly what is wanted.
 */
const CalendarEventCard = ({ event }: CalendarEventCardProps) => {
  'use memo'

  const isCancelled = Boolean(event.cancelled)
  // Null when the event has no category; an unrecognised one still resolves,
  // with the neutral fallback colour.
  const category = getEventCategory(event.category)

  //  Event-local, not browser-local: the card sits in a cell keyed by the
  //  event's own day, so showing the viewer's clock would put a 7pm Vancouver
  //  event under "10pm" in a cell labelled the previous date. This is the same
  //  rule `CalendarGameCard` follows with its venue zone.
  const start = event.timezone
    ? instantInZone(event.starts_at, event.timezone)
    : DateTime.fromISO(event.starts_at)
  const time = start.isValid ? start.toFormat('h:mm a') : ''

  // Same venue/facility/address triple games carry, formatted identically, so
  // the location line reads the same on both kinds of card.
  const location = formatEventLocation(event)

  return (
    <Link to={`${ROUTES.EVENT_ROOT}/${event.id}`} css={cardStyles}>
      <div css={timeRowStyles}>
        {time && <span css={timeStyles}>{time}</span>}
        {/* Cancelled takes precedence over the category: a called-off practice
            needs that state read first, exactly as the list row orders them. */}
        {isCancelled ? (
          <span css={cancelledTagStyles}>Cancelled</span>
        ) : category ? (
          <span
            css={categoryTagStyles}
            style={{
              backgroundColor: category.color,
              color: category.textColor,
            }}
            title={category.label}
          >
            {category.label}
          </span>
        ) : null}
      </div>

      <div
        css={nameRowStyles}
        //  A month cell is ~110px wide, so this line truncates there and the
        //  full name is only available on hover.
        title={event.name ?? ''}
      >
        {/*
          The same "Event:" type marker the list row puts before an event's
          name, so the two surfaces label it identically. Greyed but NOT struck
          through on a cancelled event — the strike belongs to the name;
          striking the type would read as "Event" itself being cancelled.
        */}
        <span css={[typePrefixStyles, isCancelled && typePrefixMutedStyles]}>
          Event:
        </span>
        <span css={[nameStyles, isCancelled && nameCancelledStyles]}>
          {capitalizeWords(event.name ?? '')}
        </span>
      </div>

      {/*
        Always rendered, even with nothing to say. The location is optional on
        an event, and dropping the row collapsed the card — so a day mixing
        located and unlocated events produced rows of two different heights.
        An empty row holds the line box open instead, and is hidden from
        assistive tech so a screen reader is not read a blank field.
      */}
      <div
        css={locationStyles}
        data-testid='calendar-event-location'
        aria-hidden={location ? undefined : 'true'}
      >
        <span css={locationTextStyles} title={location || undefined}>
          {/* A non-breaking space, not '': an empty span has no line box and
              would collapse to zero height, defeating the whole point. */}
          {location || '\u00A0'}
        </span>
      </div>
    </Link>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/*
 * `INFO_300` rather than PRIMARY: PRIMARY is what a timed GAME card uses, and
 * a shared border would erase the distinction this card exists to make. It is
 * also a hue, not a value — so it survives greyscale and dim displays, where a
 * background-value cue collapses.
 *
 * A fixed token, and NOT tenant-switched on purpose: PRIMARY moves between
 * amber (SportNinja) and blue (Canlan), and on Canlan an event border derived
 * from it would land on the same blue the game cards use.
 */
const cardStyles = css`
  display: block;
  text-decoration: none;
  background: ${colors.SECONDARY_400};
  border-left: 3px solid ${colors.INFO_300};
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

/*
 * The time and the badge share a line rather than stacking, for the same
 * reason the game card's do: a month cell shows ~3 cards before it starts
 * hiding them behind "+N more", and a card that grew a row would cut that to 2.
 */
const timeRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  /*
   * Held open independently of its contents. The time is 13px/1.2 and the
   * badge only 9px, so the TIME is what sets this row's height — and an event
   * whose start date will not parse renders no time at all, which would
   * collapse the row and shorten the whole card. Matches 13px x 1.2.
   */
  min-height: 15.6px;

  /* The badge gives up width first — a truncated "Skills Sess…" beside a whole
     time beats a whole category over a cut-off "3:1…". */
  > *:last-child {
    min-width: 0;
    flex: 0 1 auto;
  }
`

/*
 * `INFO_300`, matching the card's rail rather than the game card's PRIMARY
 * time. The two accents agreeing inside one card is what makes the rail read
 * as the card's identity instead of as decoration.
 */
const timeStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 13px;
  color: ${colors.INFO_300};
  line-height: 1.2;
  flex: none;
`

const badgeBaseStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 9px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/* Fill and text colour come from `getEventCategory`, applied inline — the
   palette is data, so it cannot be a static emotion rule. `textColor` is
   computed for contrast, never assumed. */
const categoryTagStyles = css`
  ${badgeBaseStyles};
`

const cancelledTagStyles = css`
  ${badgeBaseStyles};
  background: ${colors.ERROR_200};
  color: ${colors.NEUTRAL_0};
`

/*
 * Holds the "Event:" marker beside the name on one line. Flex rather than
 * inline text so the NAME is the only part that truncates — as a single run,
 * a narrow month cell would eat the marker first and leave "Ev…", losing the
 * label the row is there to carry.
 */
const nameRowStyles = css`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`

/*
 * `INFO_300`, matching this card's rail and time — and the list row, which is
 * being trialled on the same info blue. Keeping the marker on the accent the
 * rest of the card already uses means the event identity is one colour
 * everywhere rather than two competing ones.
 */
const typePrefixStyles = css`
  ${font.body}
  ${weightSemiBold}
  flex: none;
  font-size: 13px;
  line-height: 1.3;
  color: ${colors.INFO_300};
  white-space: nowrap;
`

const typePrefixMutedStyles = css`
  color: ${colors.NEUTRAL_50};
`

const nameStyles = css`
  ${font.body}
  ${weightMedium}
  min-width: 0;
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* Struck through AND muted — the strike is the unambiguous signal, the mute
   drops it below the live cards around it in the same cell. */
const nameCancelledStyles = css`
  color: ${colors.NEUTRAL_50};
  text-decoration: line-through;
`

const locationStyles = css`
  ${font.body}
  ${weightRegular}
  display: flex;
  align-items: baseline;
  min-width: 0;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  line-height: 1.2;
`

const locationTextStyles = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default CalendarEventCard
